import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardWrapper from "components/Layout/DashboardWrapper";
import AccordionCustom from "components/legacy/Accordion";
import { Combobox } from "@/components/ui/combobox";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { inputClass, selectClass } from "@/lib/formStyles";
import { openProtectedFile } from "utils/openProtectedFile";
import { roles } from "constants/roles";
import { getUsersByRole } from "services/users";
import useFinancialReport from "hooks/queries/useFinancialReport";

// ---------------------------------------------------------------------------
// Helpers locaux (formatage). Pas extraits en util partagé tant qu'on n'a
// qu'un seul consommateur ; à factoriser si d'autres pages reporting
// arrivent.
// ---------------------------------------------------------------------------

const formatFcfa = (value = 0) =>
  `${new Intl.NumberFormat("fr-FR").format(Math.round(value || 0))} FCFA`;

const formatCount = (value = 0) =>
  new Intl.NumberFormat("fr-FR").format(value || 0);

const formatPercent = (rate = 0) =>
  `${(rate * 100).toFixed(1)}%`;

// `createdAt` côté back est un ISO complet — on n'affiche que la date.
const formatInvoiceDate = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

// Format compact pour les ticks de chart (12 500 → 12,5K).
const formatCompactFcfa = (value = 0) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
};

// Pour les buckets timeseries en YYYY-MM-DD → libellé court FR.
const formatBucket = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
};

// Défaut filtre : du 1er du mois courant à aujourd'hui (un mois glissant
// est le cas d'usage le plus courant pour un cabinet, et évite un appel
// vide quand l'utilisateur arrive sur la page).
const todayIso = () => new Date().toISOString().slice(0, 10);
const firstOfMonthIso = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// Couleurs charts — adossées à la palette emerald/amber/rose/slate du
// design system. Pas de couleur out-of-system.
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  PAID: "#10b981", // emerald-500
  PENDING: "#f59e0b", // amber-500
  FAILED: "#f43f5e", // rose-500
};

const ACTS_TYPE_COLORS: Record<string, string> = {
  CONSULTATION: "#10b981", // emerald-500
  EXAM: "#0ea5e9", // sky-500
};

const BAR_PALETTE = [
  "#10b981",
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#f43f5e",
  "#64748b",
];

// Statut → libellé FR pour la lisibilité utilisateur.
const STATUS_LABELS: Record<string, string> = {
  PAID: "Payée",
  PENDING: "En attente",
  FAILED: "Échouée",
};

const ALL_STATUSES = ["PAID", "PENDING", "FAILED"];

const GROUP_BY_OPTIONS: Array<{ value: "day" | "week" | "month"; label: string }> = [
  { value: "day", label: "Jour" },
  { value: "week", label: "Semaine" },
  { value: "month", label: "Mois" },
];

// ---------------------------------------------------------------------------
// Hooks lookup (médecins + assureurs) — fetch one-shot pour alimenter les
// comboboxes filtre. Pour un cabinet de taille modeste, ramener jusqu'à 200
// résultats en un appel est OK et évite la complexité d'un combobox async.
// ---------------------------------------------------------------------------

const useDoctorsLookup = () =>
  useQuery(
    ["lookup-doctors"],
    () => getUsersByRole(roles.ROLE_DOCTOR, "", 0, 200),
    { staleTime: 5 * 60 * 1000, select: (r: any) => r?.data?.content || [] }
  );

const useInsurersLookup = () =>
  useQuery(
    ["lookup-insurers"],
    () => getUsersByRole(roles.ROLE_INSURER, "", 0, 200),
    { staleTime: 5 * 60 * 1000, select: (r: any) => r?.data?.content || [] }
  );

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

const Bilans = () => {
  // Filtres — état contrôlé. Tant qu'aucun filtre n'a été appliqué via le
  // bouton « Appliquer », on n'envoie pas la requête : un changement libre
  // de date ne déclenche pas un refetch.
  const [draftFrom, setDraftFrom] = useState(firstOfMonthIso());
  const [draftTo, setDraftTo] = useState(todayIso());
  const [draftStatuses, setDraftStatuses] = useState<string[]>([]);
  const [draftPractitionerId, setDraftPractitionerId] = useState<number | null>(
    null
  );
  const [draftInsurerId, setDraftInsurerId] = useState<number | null>(null);
  const [draftGroupBy, setDraftGroupBy] = useState<"day" | "week" | "month">(
    "day"
  );
  const [page, setPage] = useState(0);

  // « Filtres appliqués » : ce qui est effectivement envoyé au back. Mis à
  // jour explicitement via `applyFilters()` pour éviter un refetch à chaque
  // frappe de date.
  const [applied, setApplied] = useState(() => ({
    from: firstOfMonthIso(),
    to: todayIso(),
    statuses: [] as string[],
    practitionerId: null as number | null,
    insurerId: null as number | null,
    groupBy: "day" as "day" | "week" | "month",
  }));

  const applyFilters = () => {
    setApplied({
      from: draftFrom,
      to: draftTo,
      statuses: draftStatuses,
      practitionerId: draftPractitionerId,
      insurerId: draftInsurerId,
      groupBy: draftGroupBy,
    });
    setPage(0);
  };

  const resetFilters = () => {
    const from = firstOfMonthIso();
    const to = todayIso();
    setDraftFrom(from);
    setDraftTo(to);
    setDraftStatuses([]);
    setDraftPractitionerId(null);
    setDraftInsurerId(null);
    setDraftGroupBy("day");
    setApplied({
      from,
      to,
      statuses: [],
      practitionerId: null,
      insurerId: null,
      groupBy: "day",
    });
    setPage(0);
  };

  // Toggle d'un statut dans le multi-select chip.
  const toggleStatus = (status: string) => {
    setDraftStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  // Lookups pour les comboboxes de filtre.
  const { data: doctors = [] } = useDoctorsLookup();
  const { data: insurers = [] } = useInsurersLookup();

  const doctorOptions = useMemo(
    () =>
      (doctors as any[]).map((d) => ({
        value: d.id,
        label: `${d.firstName || ""} ${d.lastName || ""}`.trim() || `#${d.id}`,
      })),
    [doctors]
  );

  const insurerOptions = useMemo(
    () =>
      (insurers as any[]).map((i) => ({
        value: i.id,
        label: i.name || `#${i.id}`,
      })),
    [insurers]
  );

  // --- Requête principale -------------------------------------------------

  const { data: report, isFetching, isLoading } = useFinancialReport({
    ...applied,
    page,
    size: 20,
  });

  const summary = report?.summary;
  const byStatus = report?.byStatus || [];
  const byPractitioner = report?.byPractitioner || [];
  const byInsurer = report?.byInsurer || [];
  const byActsType = report?.byActsType || [];
  const topActs = report?.topActs || [];
  const paymentMethods = report?.paymentMethods || [];
  const timeseries = report?.timeseries || [];
  const invoicesPage = report?.invoices;
  const invoices = invoicesPage?.content || [];

  // Données chart timeseries — on garde le bucket brut pour l'axe X (Recharts
  // accepte les strings ISO) et on injecte le libellé court via tickFormatter.
  const timeseriesData = useMemo(
    () =>
      timeseries.map((t: any) => ({
        bucket: t.bucket,
        Facturé: t.totalInvoiceAmount,
        Payé: t.totalPaid,
        Reste: t.totalRemaining,
      })),
    [timeseries]
  );

  // Pie data pour les donuts — formatage du libellé pour la légende +
  // tooltip. `nameKey` recharts pointe sur `name`, `dataKey` sur `value`.
  const statusPie = useMemo(
    () =>
      byStatus.map((s) => ({
        name: STATUS_LABELS[s.status] || s.status,
        rawStatus: s.status,
        value: s.totalInvoiceAmount,
        count: s.count,
      })),
    [byStatus]
  );

  const actsTypePie = useMemo(
    () =>
      byActsType.map((t) => ({
        name: t.actsTypeName,
        value: t.revenue,
        lineCount: t.lineCount,
      })),
    [byActsType]
  );

  // Top 5 praticiens / assureurs pour les bars (lisibilité).
  const practitionerBars = useMemo(
    () =>
      [...byPractitioner]
        .sort((a, b) => b.totalInvoiceAmount - a.totalInvoiceAmount)
        .slice(0, 5)
        .map((p) => ({
          name:
            `${p.practitionerFirstName || ""} ${
              p.practitionerLastName || ""
            }`.trim() || `#${p.practitionerId}`,
          Facturé: p.totalInvoiceAmount,
          Payé: p.totalPaid,
          Reste: p.totalRemaining,
        })),
    [byPractitioner]
  );

  const insurerBars = useMemo(
    () =>
      [...byInsurer]
        .sort(
          (a, b) =>
            (b.totalInsurerCoverage || 0) +
            (b.totalPatientPart || 0) -
            ((a.totalInsurerCoverage || 0) + (a.totalPatientPart || 0))
        )
        .slice(0, 5)
        .map((i) => ({
          name: i.insurerName || `#${i.insurerId}`,
          "Part assureur": i.totalInsurerCoverage,
          "Part patient": i.totalPatientPart,
        })),
    [byInsurer]
  );

  const paymentMethodBars = useMemo(
    () =>
      paymentMethods.map((p) => ({
        name: p.method || "—",
        Montant: p.totalAmount,
        count: p.count,
      })),
    [paymentMethods]
  );

  return (
    <DashboardWrapper showSpinner={isLoading}>
      <>
        <div className="space-y-8">
          {/* Hero */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-100 rounded-[28px] border border-slate-200 bg-white shadow-sm p-6 md:p-8">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                <i className="bx bx-bar-chart-alt-2 text-base" />
                Reporting financier
              </span>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
                Bilans &amp; performance
              </h1>
              <p className="text-sm text-slate-500 max-w-2xl">
                Visualisez l&apos;activité, les recouvrements et la
                répartition par praticien, assureur ou type d&apos;acte sur
                la période sélectionnée.
              </p>
            </div>
          </section>

          {/* Filters */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-150 rounded-2xl border border-slate-200 bg-white shadow-sm p-5 md:p-6 space-y-5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Filtres
              </span>
              <span className="flex-1 h-px bg-slate-100" />
              {isFetching && !isLoading && (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
                  <i className="bx bx-loader-alt bx-spin" /> Mise à jour…
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Du
                </span>
                <input
                  type="date"
                  value={draftFrom}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Au
                </span>
                <input
                  type="date"
                  value={draftTo}
                  onChange={(e) => setDraftTo(e.target.value)}
                  className={inputClass}
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Praticien
                </span>
                <div className="mt-1">
                  <Combobox
                    options={[
                      { value: null, label: "Tous les praticiens" },
                      ...doctorOptions,
                    ]}
                    value={draftPractitionerId}
                    onChange={(opt: any) =>
                      setDraftPractitionerId(opt?.value ?? null)
                    }
                    placeholder="Tous les praticiens"
                    searchPlaceholder="Rechercher un praticien…"
                    className="w-full"
                  />
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Assureur
                </span>
                <div className="mt-1">
                  <Combobox
                    options={[
                      { value: null, label: "Tous les assureurs" },
                      ...insurerOptions,
                    ]}
                    value={draftInsurerId}
                    onChange={(opt: any) =>
                      setDraftInsurerId(opt?.value ?? null)
                    }
                    placeholder="Tous les assureurs"
                    searchPlaceholder="Rechercher un assureur…"
                    className="w-full"
                  />
                </div>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
              <div className="space-y-2">
                <span className="block text-sm font-medium text-slate-600">
                  Statut des factures
                </span>
                <div className="flex flex-wrap gap-2">
                  {ALL_STATUSES.map((s) => {
                    const active = draftStatuses.includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleStatus(s)}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                          active
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        )}
                      >
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: STATUS_COLORS[s] }}
                        />
                        {STATUS_LABELS[s]}
                      </button>
                    );
                  })}
                  {draftStatuses.length === 0 && (
                    <span className="text-xs text-slate-400 self-center">
                      Tous les statuts inclus
                    </span>
                  )}
                </div>
              </div>

              <label className="block">
                <span className="block text-sm font-medium text-slate-600">
                  Granularité
                </span>
                <div className="relative">
                  <select
                    value={draftGroupBy}
                    onChange={(e) =>
                      setDraftGroupBy(
                        e.target.value as "day" | "week" | "month"
                      )
                    }
                    className={selectClass}
                  >
                    {GROUP_BY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
                </div>
              </label>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 h-10 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <i className="bx bx-rotate-left text-base" />
                Réinitialiser
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 px-5 h-10 text-sm font-medium text-white shadow-lg shadow-green-600/25 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-emerald-500/30"
              >
                <i className="bx bx-search-alt-2 text-base" />
                Appliquer les filtres
              </button>
            </div>
          </section>

          {/* KPIs principaux */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-200 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard
              tone="slate"
              icon="bx-receipt"
              label="Montant brut"
              value={formatFcfa(summary?.totalBrut)}
              footnote={`${formatCount(summary?.invoiceCount)} facture${
                (summary?.invoiceCount || 0) > 1 ? "s" : ""
              }`}
            />
            <KpiCard
              tone="emerald"
              icon="bx-check-circle"
              label="Encaissé"
              value={formatFcfa(summary?.totalPaid)}
              footnote={`Taux : ${formatPercent(summary?.collectionRate)}`}
            />
            <KpiCard
              tone="rose"
              icon="bx-error-circle"
              label="Reste à percevoir"
              value={formatFcfa(summary?.totalRemaining)}
              footnote={`${formatCount(
                summary?.pendingCount
              )} en attente`}
            />
            <KpiCard
              tone="sky"
              icon="bx-trending-up"
              label="Panier moyen"
              value={formatFcfa(summary?.averageInvoiceAmount)}
              footnote={`Couverture assureur ${formatFcfa(
                summary?.totalInsurerCoverage
              )}`}
            />
          </section>

          {/* Mini stats statuts (4 chiffres en bandeau) */}
          <section className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both delay-250 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatusBadge
              tone="slate"
              label="Total factures"
              count={summary?.invoiceCount || 0}
            />
            <StatusBadge
              tone="emerald"
              label="Payées"
              count={summary?.paidCount || 0}
            />
            <StatusBadge
              tone="amber"
              label="En attente"
              count={summary?.pendingCount || 0}
            />
            <StatusBadge
              tone="rose"
              label="Échouées"
              count={summary?.failedCount || 0}
            />
          </section>

          {/* Timeseries */}
          <ChartCard
            title="Évolution"
            subtitle={`Granularité : ${
              GROUP_BY_OPTIONS.find((o) => o.value === applied.groupBy)?.label
            } · ${timeseries.length} point${timeseries.length > 1 ? "s" : ""}`}
            empty={timeseries.length === 0}
            emptyHint="Aucune activité sur la période — élargissez les dates."
          >
            <ResponsiveContainer width="100%" height={280}>
              <LineChart
                data={timeseriesData}
                margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
              >
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis
                  dataKey="bucket"
                  tickFormatter={formatBucket}
                  stroke="#94a3b8"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={formatCompactFcfa}
                  stroke="#94a3b8"
                  tick={{ fontSize: 11 }}
                  width={48}
                />
                <Tooltip
                  formatter={(v: any) => formatFcfa(Number(v))}
                  labelFormatter={(label: any) => formatBucket(label)}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="Facturé"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="Payé"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="Reste"
                  stroke="#f43f5e"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Donuts statuts + types d'acte */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              title="Répartition par statut"
              subtitle="Montant facturé · part de chaque statut"
              empty={statusPie.length === 0}
            >
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={statusPie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {statusPie.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={
                          STATUS_COLORS[entry.rawStatus] ||
                          BAR_PALETTE[idx % BAR_PALETTE.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: any, _n: any, p: any) => [
                      formatFcfa(Number(v)),
                      `${p?.payload?.name} · ${formatCount(
                        p?.payload?.count
                      )} factures`,
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Répartition par type d'acte"
              subtitle="Revenus · consultation vs examen"
              empty={actsTypePie.length === 0}
            >
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={actsTypePie}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {actsTypePie.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={
                          ACTS_TYPE_COLORS[entry.name] ||
                          BAR_PALETTE[idx % BAR_PALETTE.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v: any, _n: any, p: any) => [
                      formatFcfa(Number(v)),
                      `${p?.payload?.name} · ${formatCount(
                        p?.payload?.lineCount
                      )} lignes`,
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          {/* Bars praticiens / assureurs */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              title="Top praticiens"
              subtitle="Montant facturé · payé · reste"
              empty={practitionerBars.length === 0}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={practitionerBars}
                  layout="vertical"
                  margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={formatCompactFcfa}
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    width={120}
                  />
                  <Tooltip
                    formatter={(v: any) => formatFcfa(Number(v))}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Facturé" fill="#0ea5e9" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Payé" fill="#10b981" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Reste" fill="#f43f5e" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Top assureurs"
              subtitle="Part assureur vs part patient"
              empty={insurerBars.length === 0}
            >
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={insurerBars}
                  layout="vertical"
                  margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tickFormatter={formatCompactFcfa}
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    width={120}
                  />
                  <Tooltip
                    formatter={(v: any) => formatFcfa(Number(v))}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="Part assureur"
                    fill="#10b981"
                    radius={[0, 4, 4, 0]}
                  />
                  <Bar
                    dataKey="Part patient"
                    fill="#f59e0b"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </section>

          {/* Payment methods + Top acts */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              title="Méthodes de paiement"
              subtitle="Montant encaissé par canal"
              empty={paymentMethodBars.length === 0}
            >
              <ResponsiveContainer width="100%" height={240}>
                <BarChart
                  data={paymentMethodBars}
                  margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
                >
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={formatCompactFcfa}
                    stroke="#94a3b8"
                    tick={{ fontSize: 11 }}
                    width={48}
                  />
                  <Tooltip
                    formatter={(v: any, _n: any, p: any) => [
                      formatFcfa(Number(v)),
                      `${p?.payload?.name} · ${formatCount(
                        p?.payload?.count
                      )} transactions`,
                    ]}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="Montant" radius={[6, 6, 0, 0]}>
                    {paymentMethodBars.map((_entry, idx) => (
                      <Cell
                        key={idx}
                        fill={BAR_PALETTE[idx % BAR_PALETTE.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  Top actes
                </span>
                <span className="flex-1 h-px bg-slate-100" />
                <span className="text-xs text-slate-400">
                  {topActs.length} acte{topActs.length > 1 ? "s" : ""}
                </span>
              </div>
              {topActs.length === 0 ? (
                <div className="py-10 text-center text-sm text-slate-400">
                  Pas de données.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100">
                        <th className="py-2 text-left">Acte</th>
                        <th className="py-2 text-left">Type</th>
                        <th className="py-2 text-right">Qté</th>
                        <th className="py-2 text-right">Revenu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topActs.slice(0, 8).map((a) => (
                        <tr
                          key={a.actsId}
                          className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/30 transition-colors"
                        >
                          <td className="py-2.5 text-slate-800 font-medium">
                            {a.actsLabel || "—"}
                          </td>
                          <td className="py-2.5">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                                a.actsTypeName === "CONSULTATION"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                  : "bg-sky-50 text-sky-700 border border-sky-100"
                              )}
                            >
                              {a.actsTypeName}
                            </span>
                          </td>
                          <td className="py-2.5 text-right tabular-nums text-slate-700">
                            {formatCount(a.quantity)}
                          </td>
                          <td className="py-2.5 text-right tabular-nums font-semibold text-emerald-700">
                            {formatFcfa(a.revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          {/* Liste des factures */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                Factures de la période
              </h2>
              <span className="text-xs text-slate-400">
                {formatCount(invoicesPage?.totalElements || invoices.length)}{" "}
                au total
              </span>
            </div>

            {invoices.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 mb-3">
                  <i className="bx bx-receipt text-2xl" />
                </div>
                <p className="text-sm font-medium text-slate-700">
                  Aucune facture pour la période
                </p>
                <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                  Élargissez la plage de dates ou levez les filtres pour voir
                  plus de résultats.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice: any, idx: number) => (
                  <InvoiceRow key={invoice?.id ?? idx} invoice={invoice} />
                ))}
              </div>
            )}

            {invoicesPage && (invoicesPage.totalPages || 0) > 1 && (
              <Pagination
                currentPage={invoicesPage.currentPage ?? page}
                totalPages={invoicesPage.totalPages || 0}
                totalElements={invoicesPage.totalElements || 0}
                currentCount={invoices.length}
                hasNext={invoicesPage.hasNext || false}
                hasPrevious={invoicesPage.hasPrevious || false}
                onPageChange={setPage}
                itemLabel="facture"
                isLoading={isFetching}
              />
            )}
          </section>
        </div>
      </>
    </DashboardWrapper>
  );
};

// ---------------------------------------------------------------------------
// Sous-composants présentationnels
// ---------------------------------------------------------------------------

const kpiToneMap: Record<string, string> = {
  slate: "from-slate-50 to-white border-slate-200 [&_.kpi-icon]:text-slate-500",
  emerald:
    "from-emerald-50 to-white border-emerald-100 [&_.kpi-icon]:text-emerald-600",
  rose: "from-rose-50 to-white border-rose-100 [&_.kpi-icon]:text-rose-500",
  sky: "from-sky-50 to-white border-sky-100 [&_.kpi-icon]:text-sky-600",
};

const KpiCard: React.FC<{
  tone: "slate" | "emerald" | "rose" | "sky";
  icon: string;
  label: string;
  value: string;
  footnote?: string;
}> = ({ tone, icon, label, value, footnote }) => (
  <div
    className={cn(
      "rounded-2xl border bg-gradient-to-br p-5 flex items-start justify-between gap-4 shadow-sm",
      kpiToneMap[tone]
    )}
  >
    <div className="min-w-0 leading-tight">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </div>
      <div className="mt-1.5 text-xl md:text-2xl font-semibold text-slate-900 tabular-nums">
        {value}
      </div>
      {footnote && (
        <div className="mt-1 text-[11px] text-slate-400 truncate">
          {footnote}
        </div>
      )}
    </div>
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-100">
      <i className={cn("kpi-icon bx text-xl", icon)} />
    </div>
  </div>
);

const statusBadgeTone: Record<string, string> = {
  slate: "border-slate-200 bg-slate-50 text-slate-700",
  emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
  amber: "border-amber-100 bg-amber-50 text-amber-700",
  rose: "border-rose-100 bg-rose-50 text-rose-700",
};

const StatusBadge: React.FC<{
  tone: "slate" | "emerald" | "amber" | "rose";
  label: string;
  count: number;
}> = ({ tone, label, count }) => (
  <div
    className={cn(
      "rounded-xl border px-4 py-3 flex items-center justify-between gap-3",
      statusBadgeTone[tone]
    )}
  >
    <span className="text-xs font-medium">{label}</span>
    <span className="text-lg font-semibold tabular-nums">
      {formatCount(count)}
    </span>
  </div>
);

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  empty?: boolean;
  emptyHint?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, empty, emptyHint, children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 md:p-6">
    <div className="flex items-baseline justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {subtitle && (
          <p className="text-xs text-slate-400 mt-0.5 truncate">{subtitle}</p>
        )}
      </div>
    </div>
    {empty ? (
      <div className="py-10 text-center text-sm text-slate-400">
        {emptyHint || "Pas de données."}
      </div>
    ) : (
      children
    )}
  </div>
);

// Ligne facture en accordéon (réutilise le shape `InvoiceResponse` standard
// + ajoute un fallback `actsLabel` pour la cellule Acte si jamais le DTO
// page-projeté livre du flat plutôt que du nested. Aucune logique de
// paiement ici — la liste est read-only depuis le bilan.
const InvoiceRow: React.FC<{ invoice: any }> = ({ invoice }) => {
  const paymentStatus = invoice?.paymentStatus || "PENDING";
  return (
    <AccordionCustom
      title={
        <div className="flex items-center gap-3 flex-wrap min-w-0 w-full">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm ring-2 ring-white">
            <i className="bx bx-receipt text-base" />
          </span>
          <div className="flex flex-col min-w-0">
            <span className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
              Facturation
            </span>
            <span className="text-sm font-semibold text-slate-800 truncate">
              {formatInvoiceDate(invoice?.createdAt)}
            </span>
          </div>
          {invoice?.patient && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
              <i className="bx bx-user text-sm text-slate-400" />
              {`${invoice.patient.firstName || ""} ${
                invoice.patient.lastName || ""
              }`.trim() || `#${invoice.patient.id}`}
            </span>
          )}
          <span
            className={cn(
              "ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium",
              paymentStatus === "PAID"
                ? "border-emerald-100 bg-emerald-50 text-emerald-700"
                : paymentStatus === "FAILED"
                ? "border-rose-100 bg-rose-50 text-rose-700"
                : "border-amber-200 bg-amber-50 text-amber-700"
            )}
          >
            <i
              className={
                paymentStatus === "PAID"
                  ? "bx bx-check-circle text-sm"
                  : paymentStatus === "FAILED"
                  ? "bx bx-x-circle text-sm"
                  : "bx bx-time-five text-sm"
              }
            />
            {STATUS_LABELS[paymentStatus] || paymentStatus}
          </span>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="px-3 py-3 text-left">Acte</th>
                  <th className="px-3 py-3 text-right">Qté</th>
                  <th className="px-3 py-3 text-right">P.U</th>
                  <th className="px-3 py-3 text-right">% PC</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-right">Mt. Fact.</th>
                  <th className="px-3 py-3 text-right">Mt. Payé</th>
                  <th className="px-3 py-3 text-right">Reste</th>
                </tr>
              </thead>
              <tbody>
                {(invoice?.invoiceActsList || []).map(
                  (item: any, j: number) => (
                    <tr
                      key={j}
                      className="border-t border-slate-100 text-slate-600 hover:bg-emerald-50/40 transition-colors"
                    >
                      <td className="px-3 py-3 whitespace-nowrap font-medium text-slate-700">
                        {item?.acts?.label || item?.actsLabel || "—"}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right">
                        {formatCount(item?.quantity)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right tabular-nums">
                        {formatCount(item?.price)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right">
                        {item?.insurerCoverage ?? 0}%
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right tabular-nums">
                        {formatCount(item?.totalInvoice)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-emerald-600">
                        {formatCount(item?.invoiceAmount)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-slate-700">
                        {formatCount(item?.amountPaid)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right tabular-nums font-semibold text-rose-500">
                        {formatCount(item?.remainingPaid || 0)}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (!invoice?.id) return;
              openProtectedFile(`/invoice/invoice-pdf/${invoice.id}`, {
                loadingMessage: "Téléchargement de la facture…",
              });
            }}
            disabled={!invoice?.id}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 h-10 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 hover:text-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i className="bx bx-download text-base" />
            Télécharger le PDF
          </button>
        </div>
      </div>
    </AccordionCustom>
  );
};

export default Bilans;
